// Normalizador do documento RDO legado no BOUNDARY DE LEITURA.
//
// O RDO é guardado como jsonb livre em `rdos.payload`. Payloads antigos (ou
// gravados por versões anteriores da UI) podem não ter arrays/objetos que as
// telas assumem existir — e vários consumidores fazem `.map()`/`.reduce()`
// direto (RDOPublic, RDOPrint, RDO.tsx). Passar todo RDO por aqui garante o
// formato e elimina a classe inteira de crashes por undefined/null.
//
// Regra: normalizar SEM inventar dado. Campos ausentes viram vazio (array/
// string vazia) ou zero, nunca um valor fictício.
import type { RDO } from "@/services/rdoStorageService";

const asArray = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const asNumber = (v: unknown, fallback = 0): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;
const asString = (v: unknown, fallback = ""): string =>
  typeof v === "string" ? v : fallback;

type Workforce = RDO["workforce"][number];
type Equipment = RDO["equipment"][number];
type Activity = RDO["activities"][number];
type Occurrence = NonNullable<RDO["occurrences"]>[number];
type Comment = NonNullable<RDO["comments"]>[number];

function normalizeWorkforce(raw: unknown): RDO["workforce"] {
  return asArray<Record<string, unknown>>(raw).map((w) => ({
    ...(w as Workforce),
    // RDOPublic soma quantity/workedHours/extraHours — sem default vira NaN
    quantity: asNumber(w.quantity, 1),
    workedHours: asNumber(w.workedHours),
    extraHours: asNumber(w.extraHours),
  }));
}

function normalizeEquipment(raw: unknown): RDO["equipment"] {
  return asArray<Record<string, unknown>>(raw).map((e) => ({
    ...(e as Equipment),
    workedHours: asNumber(e.workedHours),
    idleTime: asNumber(e.idleTime),
  }));
}

function normalizeActivities(raw: unknown): RDO["activities"] {
  return asArray<Record<string, unknown>>(raw).map((a) => ({
    ...(a as Activity),
    activity: asString(a.activity),
    completion: asNumber(a.completion),
  }));
}

function normalizeOccurrences(raw: unknown): RDO["occurrences"] {
  return asArray<Record<string, unknown>>(raw).map((o) => ({
    ...(o as Occurrence),
    // RDOPublic faz item.photos.map(...)
    photos: asArray(o.photos),
  }));
}

function normalizeComments(raw: unknown): RDO["comments"] {
  return asArray<Record<string, unknown>>(raw).map((c) => ({
    ...(c as Comment),
    // RDOPublic faz comment.attachments.length
    attachments: asArray((c as { attachments?: unknown }).attachments),
  })) as RDO["comments"];
}

// Payloads antigos guardavam materiais num array plano, cada item marcado por
// `kind`/`tipo`/`materialType`. Sem repartir aqui, esses itens sumiam da tela.
function isUsed(item: Record<string, unknown>): boolean {
  const marker = asString(item.kind ?? item.tipo ?? item.materialType).toLowerCase();
  return marker === "used" || marker === "utilizado";
}

function normalizeMaterials(raw: unknown): RDO["materials"] {
  if (Array.isArray(raw)) {
    const flat = raw as Record<string, unknown>[];
    return {
      received: flat.filter((m) => !isUsed(m)) as RDO["materials"]["received"],
      used: flat.filter(isUsed) as RDO["materials"]["used"],
    };
  }
  const m = (raw ?? {}) as { received?: unknown; used?: unknown };
  return {
    received: asArray(m.received),
    used: asArray(m.used),
  };
}

function normalizeWeather(raw: unknown): RDO["weather"] {
  const w = (raw ?? {}) as { morning?: unknown; afternoon?: unknown };
  return {
    morning: (w.morning ?? {}) as NonNullable<RDO["weather"]>["morning"],
    afternoon: (w.afternoon ?? {}) as NonNullable<RDO["weather"]>["afternoon"],
  };
}

const VALID_STATUS: ReadonlyArray<NonNullable<RDO["status"]>> = [
  "draft",
  "in_review",
  "signed",
  "approved",
];

/**
 * Garante o formato do documento RDO legado, qualquer que seja a idade do
 * payload. Sempre retorna arrays presentes e números numéricos.
 */
export function normalizeRDO(raw: Partial<RDO> & Record<string, unknown>): RDO {
  const status = VALID_STATUS.includes(raw.status as NonNullable<RDO["status"]>)
    ? (raw.status as RDO["status"])
    : "draft";

  return {
    ...(raw as RDO),
    id: asString(raw.id),
    obraId: asString(raw.obraId),
    date: asString(raw.date),
    weather: normalizeWeather(raw.weather),
    workforce: normalizeWorkforce(raw.workforce),
    equipment: normalizeEquipment(raw.equipment),
    activities: normalizeActivities(raw.activities),
    occurrences: normalizeOccurrences(raw.occurrences),
    materials: normalizeMaterials(raw.materials),
    comments: normalizeComments(raw.comments),
    photos: asArray(raw.photos),
    videos: asArray(raw.videos),
    attachments: asArray(raw.attachments),
    editHistory: asArray(raw.editHistory),
    status,
    approved: raw.approved === true,
    approvedBy: raw.approvedBy ?? null,
    approvedAt: raw.approvedAt ?? null,
    createdAt: asString(raw.createdAt),
    updatedAt: asString(raw.updatedAt),
  };
}
